import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  Badge,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StoreIcon from '@mui/icons-material/Store';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AdIcon from '@mui/icons-material/Campaign';
import EventIcon from '@mui/icons-material/Event';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PremiumIcon from '@mui/icons-material/WorkspacePremium';
import InsightsIcon from '@mui/icons-material/Insights';
import { useShop } from '../../context/ShopContext';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../services/supabaseClient';
import { useNavigate } from 'react-router-dom';

const MyShops: React.FC = () => {
  const { shops, loading, refreshShops, setActiveShopId } = useShop();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openCreate, setOpenCreate] = useState(false);
  const [newShop, setNewShop] = useState({ name: '', description: '', slug: '' });
  const [creating, setCreating] = useState(false);

  const handleCreateShop = async () => {
    if (!user || !newShop.name || !newShop.slug) return;
    
    setCreating(true);
    const { error } = await supabase.from('shops').insert([
      {
        owner_id: user.id,
        name: newShop.name,
        slug: newShop.slug.toLowerCase().replace(/\s+/g, '-'),
        description: newShop.description,
      }
    ]);

    if (!error) {
      await refreshShops();
      setOpenCreate(false);
      setNewShop({ name: '', description: '', slug: '' });
    }
    setCreating(false);
  };

  const handleOpenShop = (shopId: string) => {
    setActiveShopId(shopId);
    navigate('/dashboard/products'); // Redirect to manage products once shop is selected
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">My Shops</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreate(true)}
          sx={{ borderRadius: '12px', px: 3 }}
        >
          Create New Shop
        </Button>
      </Box>

      {shops.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8, borderRadius: '16px', bgcolor: 'background.paper', border: '1px dashed grey' }}>
          <StoreIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">You don't have any shops yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Start your e-commerce journey by creating your first shop.</Typography>
          <Button variant="outlined" onClick={() => setOpenCreate(true)}>Create Shop Now</Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {shops.map((shop) => (
            <Grid item xs={12} sm={6} md={4} key={shop.id}>
              <Card sx={{ 
                borderRadius: '16px', 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 }
              }}>
                <CardMedia
                    component="img"
                    height="140"
                    image={shop.banner_url || 'https://images.unsplash.com/photo-1500673922987-e212871fec22?auto=format&fit=crop&w=400'}
                    alt={shop.name}
                    sx={{ objectFit: 'cover' }}
                  />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" fontWeight="bold">{shop.name}</Typography>
                      {shop.status === 'active' && (
                        <Tooltip title="Verified Premium Shop">
                          <PremiumIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Chip size="small" label={shop.status} color={shop.status === 'active' ? 'success' : 'default'} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: '40px', overflow: 'hidden' }}>
                    {shop.description || 'No description provided.'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Tooltip title="Active Advertisements">
                      <Badge badgeContent={0} color="primary">
                        <AdIcon color="action" />
                      </Badge>
                    </Tooltip>
                    <Tooltip title="Wealth & Market Intelligence">
                      <Badge badgeContent={0} color="success">
                        <IconButton size="small" onClick={() => navigate(`/dashboard/my-shops/${shop.id}/wealth`)}>
                          <InsightsIcon color="action" />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                    <Tooltip title="Campaign Manager">
                      <Badge badgeContent={0} color="secondary">
                        <IconButton size="small" onClick={() => navigate(`/dashboard/my-shops/${shop.id}/campaigns`)}>
                          <EventIcon color="action" />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                    <Tooltip title="Unread Notifications">
                      <Badge badgeContent={0} color="error">
                        <NotificationsIcon color="action" />
                      </Badge>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                    <Button 
                      fullWidth 
                      variant="contained" 
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => handleOpenShop(shop.id)}
                      sx={{ borderRadius: '8px' }}
                    >
                      Manage Shop
                    </Button>
                    <IconButton 
                      size="small" 
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}
                      onClick={() => navigate(`/dashboard/my-shops/${shop.id}/settings`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Shop Dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Your Shop</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Shop Name"
              value={newShop.name}
              onChange={(e) => setNewShop({ ...newShop, name: e.target.value })}
              placeholder="e.g. Fresh Organic Farm"
            />
            <TextField
              fullWidth
              label="Shop URL Slug"
              value={newShop.slug}
              onChange={(e) => setNewShop({ ...newShop, slug: e.target.value })}
              placeholder="e.g. fresh-organic-farm"
              helperText="This will be used in your shop's unique URL"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={newShop.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewShop({ ...newShop, description: e.target.value })}
              placeholder="Tell your customers about your shop..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateShop} 
            disabled={creating || !newShop.name || !newShop.slug}
          >
            {creating ? <CircularProgress size={24} /> : 'Create Shop'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyShops;
